import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';

import { Profile } from '../entities/profile.entity';

export class ProfileFormHandler {
  static readonly PROFILE_FORM_STEP_PERSONAL_INFORMATIONS =
    'profile_form_personal_informations';
  static readonly PROFILE_FORM_STEP_CONSENTS = 'profile_form_consents';

  private _form: FormGroup;
  private _formBuilder: FormBuilder = new FormBuilder();
  private _observers: Subscription = new Subscription();
  private _profile: Profile = new Profile();
  private _currentStep = '';
  private _dataChanged: Subject<any> = new Subject<any>();
  private _dataToChange: any[] = [];

  constructor(step = '') {
    this._currentStep = step;
  }

  get dataChanged(): Observable<any> {
    return this._dataChanged.asObservable();
  }

  /**
   * Gets profile form
   * @returns profile form group
   */
  public getForm(): FormGroup {
    return this._form;
  }

  /**
   * Initializes profile form following step property, then return form
   * @returns profile form group
   */
  public init(): FormGroup {
    const personalInformationsStep = this._formBuilder.group({
      professionalTitle: [null],
      firstName: ['', { validators: [Validators.required], updateOn: 'blur' }],
      lastName: ['', { validators: [Validators.required], updateOn: 'blur' }],
      professionalSpeciality: [
        null,
        { validators: [Validators.required], updateOn: 'blur' },
      ],
      haveRpps: [null, Validators.required],
      rpps: ['', { updateOn: 'blur' }],
      professionalAddress: this._formBuilder.group({
        street: ['', { updateOn: 'blur' }],
        zipCode: ['', { updateOn: 'blur' }],
        city: ['', { updateOn: 'blur' }],
      }),
    });

    const consentsStep = this._formBuilder.group({
      consentHcpCgu: [null, Validators.requiredTrue],
      consentHcpMandatoryProcessing: [null, Validators.requiredTrue],
      consentHcpOptionalFormation: [null, Validators.required],
      consentHcpOptionalNews: [null, Validators.required],
      consentHcpOptionalPanel: [null, Validators.required],
    });

    switch (this._currentStep) {
      case ProfileFormHandler.PROFILE_FORM_STEP_PERSONAL_INFORMATIONS:
        this._form = personalInformationsStep;
        break;
      case ProfileFormHandler.PROFILE_FORM_STEP_CONSENTS:
        this._form = consentsStep;
        break;
      default:
        this._form = this._formBuilder.group({
          personalInformations: personalInformationsStep,
          consents: consentsStep,
        });
        break;
    }

    // Init observers
    this.setObservers();

    return this._form;
  }

  /**
   * Sets observers
   */
  private setObservers(): void {
    if (
      !this._currentStep ||
      this._currentStep ==
        ProfileFormHandler.PROFILE_FORM_STEP_PERSONAL_INFORMATIONS
    ) {
      this._observers.add(this.onHavingRppsChange());
      this._observers.add(this.onRppsChange());
      this.onPersonalInformationsChange();
    } else if (
      this._currentStep == ProfileFormHandler.PROFILE_FORM_STEP_CONSENTS
    ) {
      this.onConsentsChange();
    }
  }

  /**
   * Update profile form using new profile entity
   * @param profile
   */
  public update(profile: Profile): void {
    this._profile = profile;

    let data = {
      personalInformations: {
        professionalTitle: profile.professionalTitle,
        firstName: profile.firstName,
        lastName: profile.lastName,
        professionalSpeciality: profile.professionalSpeciality,
        haveRpps: profile.haveRpps,
        rpps: profile.rpps,
        professionalAddress: {
          street: profile.professionalAddress.street,
          zipCode: profile.professionalAddress.zipCode,
          city: profile.professionalAddress.city,
        },
      },
      consents: {
        consentHcpCgu: profile.consentHcpCgu,
        consentHcpMandatoryProcessing: profile.consentHcpMandatoryProcessing,
        consentHcpOptionalFormation: profile.consentHcpOptionalFormation,
        consentHcpOptionalNews: profile.consentHcpOptionalNews,
        consentHcpOptionalPanel: profile.consentHcpOptionalPanel,
      },
    };

    switch (this._currentStep) {
      case ProfileFormHandler.PROFILE_FORM_STEP_PERSONAL_INFORMATIONS:
        this._form.patchValue(data.personalInformations);
        break;
      case ProfileFormHandler.PROFILE_FORM_STEP_CONSENTS:
        this._form.patchValue(data.consents);
        break;
      default:
        this._form.patchValue(data);
        break;
    }
  }

  /**
   * Observes having rpps property changing
   * @returns Subscription
   */
  private onHavingRppsChange(): Subscription {
    let form =
      this._currentStep ==
      ProfileFormHandler.PROFILE_FORM_STEP_PERSONAL_INFORMATIONS
        ? this._form
        : this._form.get('personalInformations');
    return form.get('haveRpps').valueChanges.subscribe((res) => {
      if (res) {
        form
          .get('rpps')
          .setValidators([Validators.required, Validators.maxLength(15)]);
      } else {
        form.get('rpps').reset('');
        form.get('rpps').clearValidators();
        this._dataToChange.push({
          key: 'haveRpps',
          formControl: form.get('haveRpps'),
        });
        this._dataToChange.push({ key: 'rpps', formControl: form.get('rpps') });
        this._dataChanged.next(this._dataToChange);
      }
      form.get('rpps').updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Observes rpps property changing
   * @returns Subscription
   */
  private onRppsChange(): Subscription {
    let form =
      this._currentStep ==
      ProfileFormHandler.PROFILE_FORM_STEP_PERSONAL_INFORMATIONS
        ? this._form
        : this._form.get('personalInformations');
    return form.get('rpps').valueChanges.subscribe((res) => {
      this._dataToChange.push({
        key: 'haveRpps',
        formControl: form.get('haveRpps'),
      });
      this._dataToChange.push({ key: 'rpps', formControl: form.get('rpps') });
      this._dataChanged.next(this._dataToChange);
    });
  }

  /**
   * Observes personal informations changing
   */
  private onPersonalInformationsChange(): void {
    const disabledFormControls: string[] = ["rpps", "haveRpps"];
    let form: FormGroup =
      this._currentStep == ProfileFormHandler.PROFILE_FORM_STEP_PERSONAL_INFORMATIONS
        ? this._form
        : (this._form.get("personalInformations") as FormGroup);

    for (const [key, control] of Object.entries(form.controls)) {
      if (!disabledFormControls.includes(key)) {
        this._observers.add(
          control.valueChanges.subscribe((res) => {
            this._dataChanged.next([{ key: key, formControl: control }]);
          })
        );
      }
    }
  }

  /**
   * Observes consent changing
   */
  private onConsentsChange(): void {
    let form: FormGroup =
      this._currentStep == ProfileFormHandler.PROFILE_FORM_STEP_CONSENTS
        ? this._form
        : (this._form.get("consents") as FormGroup);

    for (const [key, control] of Object.entries(form.controls)) {
      this._observers.add(
        control.valueChanges.subscribe((res) => {
          this._dataChanged.next([{ key: key, formControl: control }]);
        })
      );
    }
  }

  /**
   * Resets profile form, unsubscribes observers
   */
  public clear(): void {
    this._form.reset();
    this._observers.unsubscribe();
  }
}
